/**
 * Shared CR (WorkItem) status token map — single source of truth for WorkItemStatus
 * colour tokens used by CrBacklogTable and CrLinkCell.
 * Token-pure: no raw hex, no RGB literals.
 *
 * Contrast (QA lane 3): chip text must clear 4.5:1 on its own tint in BOTH
 * themes. The light-theme health hues (e.g. --health-amber #b96e00 ≈ 3.5:1 on
 * its 20% tint) fail on their own, so text roles mix toward --text-hi — which
 * darkens on light (text-hi is near-black) and lightens on dark (text-hi is
 * near-white). Measured ratios (light/dark on tint-over-surface):
 *   amber mix 80%  → 5.39 / 8.05     red --sem-red-text → 4.94 / 5.98
 *   violet mix 80% → 5.80 / 4.67     slate mix 80%      → 5.43 / 5.34
 *   green mix 85%  → 5.32 / 7.62     neutral text-muted → 6.24 / 7.33
 * Lime discipline: PLANNED moved off --brand/--brand-soft (lime never colours
 * data) onto the violet "resting state" voice — same hue in both themes.
 */
import type { WorkItemStatus } from '@/types/workItem';

export const AMBER_TEXT = 'color-mix(in srgb, var(--sem-amber-text) 80%, var(--text-hi))';
export const RED_TEXT = 'var(--sem-red-text)';
export const VIOLET_TEXT = 'color-mix(in srgb, var(--sem-violet) 80%, var(--text-hi))';
export const TEAL_TEXT = 'color-mix(in srgb, var(--sem-teal) 65%, var(--text-hi))';
export const GREEN_TEXT = 'color-mix(in srgb, var(--health-green) 85%, var(--text-hi))';
export const SLATE_TEXT = 'color-mix(in srgb, var(--health-slate) 80%, var(--text-hi))';
export const VIOLET_TINT = 'color-mix(in srgb, var(--sem-violet) 12%, transparent)';
export const TEAL_TINT = 'color-mix(in srgb, var(--sem-teal) 12%, transparent)';

export const CR_STATUS_TOKENS: Record<WorkItemStatus, { bg: string; fg: string }> = {
  NOT_PLANNED: { bg: 'var(--surface-inset)',    fg: 'var(--text-muted)' },
  PLANNED:     { bg: VIOLET_TINT,               fg: VIOLET_TEXT },
  ONGOING:     { bg: 'var(--health-amber-bg)',  fg: AMBER_TEXT },
  ON_HOLD:     { bg: 'var(--health-slate-bg)',  fg: SLATE_TEXT },
  DONE:        { bg: 'var(--health-green-bg)',  fg: GREEN_TEXT },
  CANCELLED:   { bg: 'var(--health-red-bg)',    fg: RED_TEXT },
};

export const CR_STATUS_OPTIONS: WorkItemStatus[] = [
  'NOT_PLANNED', 'PLANNED', 'ONGOING', 'ON_HOLD', 'DONE', 'CANCELLED',
];

/** Sentence-case display label for an UPPER_SNAKE status value (spec §1.5 —
 *  no all-caps labels): 'NOT_PLANNED' → 'Not planned'. */
export function statusLabel(status: string): string {
  const t = status.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}
